import { Button } from 'components/ui/button';
import { AppButtonProps, buttonStyle } from 'components/buttons/buttonStyle';
import { Alert, AlertDescription } from 'components/ui/alert';
import { usePostHog } from '@posthog/react';
import { useCallback, useEffect, useMemo } from 'react';
import { useForm, useWatch } from 'react-hook-form';

// Types
import { InvoiceEmailForm, InvoiceEmailTemplateType, SendableInvoice } from 'types/invoices';

// Hooks
import { SEND_SHEET } from '../sideSheet';
import { useModal } from '../useModal';
import { isSpecAttachedByDefault } from './specAttachment';

// Components
import FileInput from 'components/forms/inputs/rhf-inputs/FileInput';
import { FieldLabelsBeside } from 'components/forms/inputs/rhf-inputs/InputWrapper';
import TextAreaInput from 'components/forms/inputs/rhf-inputs/TextAreaInput';
import { useIsNewDesign } from 'components/tables/list-table/filter-bar/useIsNewDesign';
import { ModalHeader } from 'components/modals/ModalHeader';
import EmployeePickerModal from 'components/modals/employees/EmployeePickerModal';
import { officeInactiveSuffix } from 'utils/officeOptionRenderer';
import { ModalFooterSimple } from '../ModalFooterSimple';
import { useEmailManagement } from './hooks/useEmailManagement';
import { EmailFieldsSection } from './shared/EmailFieldsSection';
import { OfficeEmailSelector } from './shared/OfficeEmailSelector';
import { SubjectInput } from './shared/SubjectInput';
import { TemplateLanguageSelector } from './shared/TemplateLanguageSelector';

// Store
import {
    useGetAvailableLanguagesQuery,
    useGetInvoiceAttachmentNamesQuery,
    useGetInvoicesEmailTemplateQuery,
    useSendInvoiceEmailMessageMutation,
} from 'store/api/emails';
import { useGetAllEmployeesQuery } from 'store/api/employees';

// Util
import { PriorityHighRounded as PriorityHighRoundedIcon } from 'components/icons';
import { FormattedMessage } from 'react-intl';
import { useGetOfficesWithFiltersQuery } from 'store/api/offices.ts';
import { cn } from 'utils/cn';
import { t } from 'utils/getTranslations';
import { useClassifiers } from 'utils/useClassifiers.ts';
import { useActiveEmployees } from '../employees/useActiveEmployees.ts';
// Might not be needed. Currently backend only accepts one valid email. If its extended to allow all strings then this fucntion will filter out invalid emails.
import useAuth from 'hooks/useAuth';
import { useInvoicesRecipients } from 'pages/invoices/hooks/useInvoicesRecipients.ts';
import { EmailRecipients } from 'types/emails.ts';
import { buildHtmlForMultilineText } from 'utils/emails.ts';
import { logError } from 'utils/monitoring';
import Loader from '../../Loader.tsx';
import { useInvoiceSendValidation } from './hooks/useInvoiceSendValidation';

const SendInvoices = (params: {
    titleId?: string;
    invoices: SendableInvoice[];
    onClose: () => void;
    templateType: InvoiceEmailTemplateType;
    getIsSpecIncluded: (invoice: SendableInvoice) => boolean;
    transitionToSent?: boolean;
}) => {
    const MAX_TOTAL_SIZE = 10 * 1024 * 1024; // 10MB
    const {
        titleId = 'invoice.send-invoice',
        invoices,
        onClose,
        templateType,
        getIsSpecIncluded,
        transitionToSent,
    } = params;
    const isNew = useIsNewDesign();
    const posthog = usePostHog();
    const { user } = useAuth();
    const { data: employees = [] } = useGetAllEmployeesQuery();
    const { classifiers } = useClassifiers({ codes: ['LANGUAGE'] });
    const { data: officesData } = useGetOfficesWithFiltersQuery({
        filters: 'invoicingEmailAddress!=null;invoicingEmailAddress!=""',
        pageSize: 1000,
    });
    const offices = officesData?.content ?? [];

    // Map offices to NamedEntityMinWithEmailDto
    const officeOptions = offices.map((office) => ({
        id: office.id,
        name: `${office.name}${officeInactiveSuffix(office) ?? ''}`,
        email: office.invoicingEmailAddress,
    }));

    // Create "Me" option for current user
    const meOption = user?.workEmailAddress
        ? {
              id: -1,
              name: 'Me',
              email: user.workEmailAddress,
          }
        : null;

    // Prepend "Me" option to office options
    const emailSenderOptions = meOption ? [meOption, ...officeOptions] : officeOptions;

    const recipients: EmailRecipients = useInvoicesRecipients(invoices);

    const employeeOptions = useActiveEmployees(employees);

    const attachments: string[] = [];

    invoices.forEach((invoice) => {
        attachments.push(t('invoices.email.invoice-attachment', { invoiceNumber: invoice.invoiceNumber }));
        if (getIsSpecIncluded(invoice)) {
            attachments.push(t('invoices.email.invoice-spec-attachment', { invoiceNumber: invoice.invoiceNumber }));
        }
    });

    // Recipient-visible attachment filenames (may be tenant-customized). The form still stores/sends the
    // stable keys built above; this map only drives how the attachment chips are labelled in the modal.
    const { data: attachmentNameData = [] } = useGetInvoiceAttachmentNamesQuery(
        invoices.map((invoice) => invoice.id),
        { skip: invoices.length === 0 }
    );
    const attachmentDisplayNames = Object.fromEntries(
        attachmentNameData.map((attachment) => [attachment.name, attachment.displayName])
    );

    const { handleSubmit, control, setValue, getValues } = useForm<InvoiceEmailForm>({
        mode: 'onChange',
        defaultValues: {
            invoicedByOffice:
                invoices.length === 1 && invoices[0].invoicedByOffice?.invoicingEmailAddress
                    ? {
                          id: invoices[0].invoicedByOffice.id,
                          name: invoices[0].invoicedByOffice.name,
                          email: invoices[0].invoicedByOffice.invoicingEmailAddress,
                      }
                    : undefined,
            to: recipients.to,
            cc: recipients.cc,
            bcc: recipients.bcc,
            replyTo: [],
            files: null,
            generatedAttachments: attachments,
        },
    });

    const {
        employeePickerOpen,
        setEmployeePickerOpen,
        removeEmail,
        addEmail,
        openEmployeePicker,
        handleEmployeePickerConfirm,
    } = useEmailManagement({ setValue, getValues, employees });

    const [sendInvoiceEmailMessage, { isLoading: isSending }] = useSendInvoiceEmailMessageMutation();

    const handleSendEmail = useCallback(
        async (form: InvoiceEmailForm) => {
            return sendInvoiceEmailMessage({
                ids: invoices.map((invoice) => invoice.id.toString()),
                to: form.to,
                cc: form.cc,
                bcc: form.bcc,
                replyTo: form.replyTo,
                subject: form.subject,
                content: buildHtmlForMultilineText(form.content),
                files: form.files,
                generatedAttachments: form.generatedAttachments,
                // Send email directly for "Me" option (id === -1), otherwise send Office ID
                invoicedByOfficeId: form.invoicedByOffice?.id === -1 ? undefined : form.invoicedByOffice?.id,
                invoicingEmailAddress:
                    form.invoicedByOffice?.id === -1 ? form.invoicedByOffice?.email || undefined : undefined,
                langCode: form.customEmailTemplateLanguage?.value,
                transitionToSent,
            }).unwrap();
        },
        [invoices, sendInvoiceEmailMessage, transitionToSent]
    );

    const [to, cc, bcc, replyTo, generatedAttachments, files, customEmailTemplateLanguage] = useWatch({
        control,
        name: ['to', 'cc', 'bcc', 'replyTo', 'generatedAttachments', 'files', 'customEmailTemplateLanguage'],
    });

    // Whether the attachments are over budget is read off the attachments themselves, so it can never
    // lag a render behind them.
    const isFileUploadBlocked = useMemo(
        () => [...(files || [])].reduce((sum, file) => sum + file.size, 0) > MAX_TOTAL_SIZE,
        [MAX_TOTAL_SIZE, files]
    );

    const { data: template, isLoading } = useGetInvoicesEmailTemplateQuery(
        {
            invoiceIds: invoices.map((invoice) => invoice.id),
            langCode: customEmailTemplateLanguage?.value,
            templateType: templateType,
        },
        { skip: invoices.length === 0 }
    );

    const { data: availableLanguages } = useGetAvailableLanguagesQuery(templateType);

    // TODO(react-doctor: no-cascading-set-state): RHF setValue cascade for
    // async-loaded template fields — framework pattern.
    useEffect(() => {
        setValue('subject', template?.subject || '');
        setValue('content', template?.content || '');
        setValue('signature', template?.signature || '');
    }, [setValue, template]);

    const removeGeneratedAttachment = (deletedAttachmentName: string) => {
        const generatedAttachmentsState = getValues('generatedAttachments').filter(
            (attachment) => attachment !== deletedAttachmentName
        );
        setValue('generatedAttachments', generatedAttachmentsState, { shouldDirty: true, shouldValidate: true });
    };

    const handleOnChange = async (data: InvoiceEmailForm) => {
        if (isFileUploadBlocked) {
            return;
        }

        try {
            await handleSendEmail(data);
            posthog.capture('invoices_sent', {
                invoice_count: invoices.length,
                transition_to_sent: Boolean(transitionToSent),
            });
            onClose();
        } catch (error) {
            logError(error);
            console.error('Failed to send invoice email: ', error);
        }
    };

    return (
        <>
            <ModalHeader
                title={t(titleId, { number: (invoices.length === 1 && invoices[0].invoiceNumber) || 'none' })}
                description={
                    transitionToSent ? (
                        <FormattedMessage id="invoices.send-sheet.description" values={{ count: invoices.length }} />
                    ) : undefined
                }
                onCancel={onClose}
            />
            <div className="h-full overflow-y-auto p-6 max-sm:px-4">
                {isFileUploadBlocked ? (
                    <Alert className="border-error bg-error-light mb-6">
                        <PriorityHighRoundedIcon className="text-error size-4" />
                        <AlertDescription className="text-foreground">
                            <FormattedMessage id="invoices.summary-sidebar.file-upload-max-size" />
                        </AlertDescription>
                    </Alert>
                ) : null}
                {/* The new design's 720px sheet stacks the fields, each label beside its field, over the content. */}
                <div className="new:flex-col flex flex-row gap-6">
                    <FieldLabelsBeside value={isNew}>
                        <div className={cn('min-w-0 grow basis-5/12', isNew && 'flex flex-col gap-3')}>
                            <OfficeEmailSelector
                                control={control}
                                setValue={setValue}
                                getValues={getValues}
                                options={emailSenderOptions}
                            />

                            <EmailFieldsSection
                                control={control}
                                to={to}
                                cc={cc}
                                bcc={bcc}
                                replyTo={replyTo}
                                onRemoveEmail={removeEmail}
                                onAddEmail={addEmail}
                                onOpenEmployeePicker={openEmployeePicker}
                            />

                            <SubjectInput control={control} />

                            <FileInput
                                name="files"
                                control={control}
                                setValue={setValue}
                                label="invoices.email.attachments"
                                generatedAttachments={generatedAttachments}
                                attachmentDisplayNames={attachmentDisplayNames}
                                removeGeneratedAttachment={removeGeneratedAttachment}
                                accept="application/pdf"
                            />

                            <TemplateLanguageSelector
                                control={control}
                                setValue={setValue}
                                languageClassifier={classifiers['LANGUAGE']}
                                availableLanguages={availableLanguages}
                                preferredLanguage={invoices.length === 1 ? invoices[0].invoicePdfLanguage : undefined}
                            />
                        </div>
                    </FieldLabelsBeside>
                    <div className="min-w-0 grow basis-7/12">
                        <TextAreaInput
                            name="content"
                            control={control}
                            label="invoices.email.content"
                            minRows={13}
                            maxRows={13}
                            required
                        />
                        {template?.signature && (
                            <div className="mt-4">
                                {isLoading ? (
                                    <Loader />
                                ) : (
                                    // Server-generated signature template — content is backend-controlled, not user input.
                                    // oxlint-disable-next-line react/no-danger
                                    <div className="mt-2" dangerouslySetInnerHTML={{ __html: template.signature }} />
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
            <ModalFooterSimple
                buttons={[
                    <Button
                        {...buttonStyle({ variant: 'outlined', color: 'primary' })}
                        key="cancel"
                        onClick={onClose}
                        disabled={isSending}
                    >
                        {t('cancel')}
                    </Button>,
                    <Button
                        {...buttonStyle({ variant: 'contained', color: 'secondary' })}
                        key="invoice-send"
                        onClick={() => {
                            void handleSubmit(handleOnChange)();
                        }}
                        disabled={isFileUploadBlocked || isSending}
                    >
                        {isSending ? t('invoices.form.sending') : t('invoices.form.send')}
                    </Button>,
                ]}
            />
            <EmployeePickerModal
                open={employeePickerOpen}
                onClose={() => setEmployeePickerOpen(false)}
                onConfirm={handleEmployeePickerConfirm}
                title="forms.select-employees"
                employeeOptions={employeeOptions}
            />
        </>
    );
};

const FORM_OVERFLOW_CUSTOM_STYLE = {
    display: 'grid',
    gridTemplateRows: 'auto 1fr auto',
    width: '1008px',
};

interface UseSendInvoicesModalParams {
    invoices: SendableInvoice[];
    emailTemplateType: InvoiceEmailTemplateType;
    getIsSendable?: () => boolean;
    getIsSpecIncluded?: (invoice: SendableInvoice) => boolean;
    transitionToSent?: boolean;
}

export const useSendInvoicesModal = ({
    invoices,
    emailTemplateType,
    getIsSendable = () => true,
    getIsSpecIncluded = isSpecAttachedByDefault,
    transitionToSent,
}: UseSendInvoicesModalParams) => {
    const { openModal, closeModal } = useModal();

    const openSendInvoicesModal = useCallback(() => {
        if (getIsSendable()) {
            openModal(
                <SendInvoices
                    onClose={closeModal}
                    titleId={'invoices.send-invoice'}
                    invoices={invoices}
                    templateType={emailTemplateType}
                    getIsSpecIncluded={getIsSpecIncluded}
                    transitionToSent={transitionToSent}
                />,
                closeModal,
                FORM_OVERFLOW_CUSTOM_STYLE,
                undefined,
                SEND_SHEET
            );
        }
    }, [invoices, emailTemplateType, getIsSendable, getIsSpecIncluded, transitionToSent, openModal, closeModal]);

    return {
        openSendInvoicesModal,
    };
};

interface SendInvoicesButtonProps extends AppButtonProps {
    invoices: SendableInvoice[];
    emailTemplateType: InvoiceEmailTemplateType;
    getIsSendable?: () => boolean;
    getIsSpecIncluded?: (invoice: SendableInvoice) => boolean;
    transitionToSent?: boolean;
}

const SendInvoicesButton = (props: SendInvoicesButtonProps) => {
    const {
        invoices,
        emailTemplateType,
        getIsSendable,
        getIsSpecIncluded,
        transitionToSent,
        children,
        variant,
        color,
        size,
        className,
        ...rest
    } = props;
    const { validateAndProceed } = useInvoiceSendValidation(invoices);

    const { openSendInvoicesModal } = useSendInvoicesModal({
        invoices,
        emailTemplateType,
        getIsSendable,
        getIsSpecIncluded,
        transitionToSent,
    });

    return (
        <Button
            {...rest}
            {...buttonStyle({ variant, color, size }, cn('whitespace-nowrap', className))}
            onClick={() => {
                // Validate invoice dates → show warning modal if needed
                void validateAndProceed(() => openSendInvoicesModal());
            }}
        >
            {children}
        </Button>
    );
};

export default SendInvoicesButton;
